/*
 * The catalog's pages, grouped into the sections its header switches between.
 * Which pages a reader sees, and their counts, still come from the sidebar's
 * catalog menu: that is where permissions are resolved.
 */

export type CatalogMenuEntry = {
  link?: string;
  text: string;
  count?: number;
  hidden?: boolean;
  permission?: string;
};

export type CatalogPage = { href: string; label: string; count?: number };

export type CatalogSection = {
  id: string;
  label: string;
  pages: CatalogPage[];
};

const PRODUCTS = '/app/catalogs/catalog';
const UNMATCHED = '/app/catalogs/listings/unmatched-items';
const MATCHED = '/app/catalogs/listings/matched-items';
/* EN-3966: the self-serve Mapping tab, the one Mapping page of a V2 catalog. */
const MAPPING = '/app/catalogs/mapping';

const SECTIONS: { id: string; label: string; links: string[] }[] = [
  {
    id: 'products',
    label: 'Products',
    links: [PRODUCTS, '/app/catalogs/components'],
  },
  {
    id: 'mapping',
    label: 'Mapping',
    links: [
      MAPPING,
      UNMATCHED,
      MATCHED,
      '/app/catalogs/duplicate-listings',
      '/app/catalogs/resolve-conflict',
    ],
  },
  {
    id: 'classification',
    label: 'Classification',
    links: ['/app/catalogs/classification-guide'],
  },
  {
    id: 'carton-configuration',
    label: 'Carton Configuration',
    links: ['/app/catalogs/attributes'],
  },
];

/** Every page the catalog header links to; each needs a route in routes.tsx. */
export const CATALOG_SECTION_LINKS: readonly string[] = SECTIONS.flatMap(
  section => section.links,
);

/*
 * A V2 catalog reads only the items list, an item's page and the Mapping tab
 * from V2. The other pages read and write the legacy catalog, which V2 does
 * not see, so they hide.
 */
const SHOWN_IN_V2 = new Set([PRODUCTS, MAPPING]);

/* The Mapping tab reads and writes V2 only: a legacy catalog keeps its own mapping pages. */
const ONLY_IN_V2 = new Set([MAPPING]);

/*
 * Matched listings has no menu entry of its own; it goes with unmatched. So
 * does the Mapping tab: whoever may map listings may use it.
 */
const SHARES_ENTRY_WITH: Record<string, string> = {
  [MATCHED]: UNMATCHED,
  [MAPPING]: UNMATCHED,
};
const LABELS: Record<string, string> = {
  [PRODUCTS]: 'Items',
  [MAPPING]: 'Mapping',
  [UNMATCHED]: 'Unmatched',
  [MATCHED]: 'Matched',
};

export function buildCatalogSections(
  entries: CatalogMenuEntry[],
  pathname: string,
  hiddenPermission: string,
  isV2 = false,
): CatalogSection[] {
  const byLink = new Map(entries.map(entry => [entry.link, entry]));

  return SECTIONS.map(section => ({
    id: section.id,
    label: section.label,
    pages: section.links.flatMap(link => {
      if (isV2 ? !SHOWN_IN_V2.has(link) : ONLY_IN_V2.has(link)) {
        return [];
      }

      const entry = byLink.get(SHARES_ENTRY_WITH[link] ?? link);
      const isOpen = isOnPage(pathname, link);
      const isAllowed =
        !!entry?.permission && entry.permission !== hiddenPermission;

      /* A page with nothing in it hides, unless the reader is already on it. */
      if (!entry || !isAllowed || (entry.hidden && !isOpen)) {
        return [];
      }

      return [
        {
          href: link,
          label: LABELS[link] ?? entry.text,
          count: SHARES_ENTRY_WITH[link] ? undefined : entry.count,
        },
      ];
    }),
  })).filter(section => section.pages.length > 0);
}

export function isOnPage(pathname: string, href: string) {
  return pathname === href || pathname.startsWith(`${href}/`);
}
